/**
 * The function returns standard settings for all Data Pages tables.
 * These settings can be overridden in the settings of each table
 */
import { formatNumberAbbreviated, tooltipRenderer } from './helpers';
import { onPopupDialogCreated } from './integrated-chart-popup';

function defaultTablesOptions(ref) {
    //
    let footnote = {};
    if (window.dataPagesIntegratedChartFootnote) {
        footnote = {
            enabled: true,
            text: window.dataPagesIntegratedChartFootnote,
        };
    }

    //
    return {
        defaultColDef: {
            sortable: true,
            resizable: true,
            flex: 1,
            floatingFilter: true,
            wrapText: true,
            autoHeight: true,
            sortingOrder: ['desc', 'asc'],
            // prevent the columns from dragging for touch devices
            // suppressMovable: window.matchMedia('(hover: none)').matches,
            // lockPosition: window.matchMedia('(hover: none)').matches,
        },
        onFirstDataRendered: (event) => {
            headerHeightSetter(event);
            setAdvancedFilterPlaceholder();
            headerScrollSynchronizer();
        },
        onAdvancedFilterBuilderVisibleChanged: () => {
            onPopupDialogCreated();
        },
        onChartCreated: () => {
            onPopupDialogCreated();
        },
        onColumnResized: headerHeightSetter,
        chartToolPanelsDef: {
            defaultToolPanel: 'data',
        },
        pagination: true,
        paginationPageSize: 50,
        enableCharts: true,
        enableRangeSelection: true,
        chartThemes: ['ag-default'],
        sideBar: {
            toolPanels: [
                {
                    id: 'filters',
                    labelDefault: 'Filters',
                    labelKey: 'filters',
                    iconKey: 'filter',
                    toolPanel: 'agFiltersToolPanel',
                },
                {
                    id: 'columns',
                    labelDefault: 'Columns',
                    labelKey: 'columns',
                    iconKey: 'columns',
                    toolPanel: 'agColumnsToolPanel',
                    toolPanelParams: {
                        suppressRowGroups: true,
                        suppressValues: true,
                        suppressPivots: true,
                        suppressPivotMode: true,
                        // suppressColumnFilter: true,
                        // suppressColumnSelectAll: true,
                        // suppressColumnExpandAll: true,
                    },
                },
            ],
        },
        suppressClipboardPaste: true,
        getContextMenuItems: function (params) {
            // Disables the Copy, Paste, Export right mouse click
            let items = params.defaultItems;
            if (items) {
                [
                    'cut',
                    'copy',
                    'copyWithHeaders',
                    'copyWithGroupHeaders',
                    'paste',
                    'export',
                ].forEach(function (value) {
                    let index = items.indexOf(value);
                    if (index !== -1) {
                        items.splice(index, 1);
                    }
                });
            }
            return items;
        },
        domLayout: 'autoHeight',
        allowContextMenuWithControlKey: true,
        //
        excelStyles: [
            {
                id: 'DateString',
                dataType: 'DateTime',
                numberFormat: {
                    format: 'yyyy-mm-dd',
                },
            },
        ],
        // Options for Integrated Charts
        // Reference for chartThemeOverrides
        // https://www.ag-grid.com/javascript-charts/api-themes/
        chartThemeOverrides: {
            common: {
                footnote: footnote,
                navigator: {
                    enabled: true,
                    height: 20,
                    margin: 25,
                },
                axes: {
                    number: {
                        label: {
                            formatter: (param) => {
                                return formatNumberAbbreviated(param.value);
                            },
                        },
                    },
                    category: {
                        label: {
                            formatter: (param) => {
                                const rowData = ref.getRowData();
                                for (let i = 0; i < rowData.length; i++) {
                                    const row = rowData[i];
                                    const date =
                                        row.begin_date ??
                                        row.begin_datetime ??
                                        row.month_begin_date;
                                    if (date === param.value) {
                                        return row.date_string;
                                    }
                                }
                                return param.value;
                            },
                        },
                    },
                },
            },
            // Tooltip renderer is not working for "common"
            bar: {
                series: {
                    tooltip: { renderer: tooltipRenderer },
                    strokeWidth: 0,
                    highlightStyle: {
                        item: {
                            strokeWidth: 1,
                        },
                        series: {
                            strokeWidth: 1,
                            dimOpacity: 0.2,
                        },
                    },
                },
            },
            line: {
                series: {
                    tooltip: { renderer: tooltipRenderer },
                },
            },
            area: {
                series: {
                    tooltip: { renderer: tooltipRenderer },
                },
            },
            pie: {
                series: {
                    tooltip: {
                        renderer: (params) => {
                            const tooltipTitle =
                                params.datum[params.calloutLabelKey];
                            const tooltipValue =
                                params.datum[params.sectorLabelKey];

                            return tooltipRenderer(
                                params,
                                tooltipTitle,
                                '',
                                formatNumberAbbreviated(tooltipValue),
                            );
                        },
                    },
                },
            },
            histogram: {
                series: {
                    tooltip: {
                        renderer: (params) => {
                            const startValue = formatNumberAbbreviated(
                                params.datum.domain[0],
                            );
                            const endValue = formatNumberAbbreviated(
                                params.datum.domain[1],
                            );
                            const tooltipTitle = `${params.xName}: ${startValue} - ${endValue}`;
                            //
                            const tooltipKey = '<b>Frequency</b>';
                            const tooltipValue = params.datum.frequency;
                            //
                            return tooltipRenderer(
                                params,
                                tooltipTitle,
                                tooltipKey,
                                tooltipValue,
                            );
                        },
                    },
                },
            },
        },
    };
}

// Header height configuration
// https://blog.ag-grid.com/wrapping-column-header-text/#cell-wrapping
function headerHeightSetter(event) {
    let padding = 60;
    let height = headerHeightGetter() + padding;
    // event.api.setHeaderHeight(height);
    event.api.setGridOption('headerHeight', height);
}

function headerHeightGetter() {
    let columnHeaderTexts = [
        ...document.querySelectorAll('.ag-header-cell-text'),
    ];
    let clientHeights = columnHeaderTexts.map(
        (headerText) => headerText.clientHeight,
    );
    return Math.max(...clientHeights);
}
function setAdvancedFilterPlaceholder() {
    const advancedFilterInput = document.querySelector(
        '.ag-advanced-filter .ag-input-field-input',
    );
    if (!advancedFilterInput) {
        return;
    }
    advancedFilterInput.setAttribute(
        'placeholder',
        'Create your custom filter...',
    );
}

function headerScrollSynchronizer() {
    const headerViewport = document.querySelector('.ag-header-viewport');
    const centerViewport = document.querySelector('.ag-center-cols-viewport');

    headerViewport.addEventListener('scroll', function () {
        centerViewport.scrollLeft = headerViewport.scrollLeft;
    });
}

export default defaultTablesOptions;
